import type { ViewCuratorMode, ViewDetail, ViewPreview } from '@deskworks/shared-types';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate, useParams } from '@tanstack/react-router';
import { AlertTriangle, Bot, Check, ChevronLeft, CircleCheck, Code2, History, LayoutDashboard, Loader2, MessageSquareText, MessagesSquare, Play, Plus, RotateCcw, Save, Settings2, Sparkles, Trash2, X } from 'lucide-react';
import { useState } from 'react';
import { Confirm } from '@/components/confirm';
import { Field } from '@/components/field';
import { Page } from '@/components/page';
import { RelativeTime } from '@/components/relative-time';
import { ErrorState, LoadingPage } from '@/components/states';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Textarea } from '@/components/ui/textarea';
import { ViewHost, viewKeys } from '@/components/views/view-host';
import { api, errorMessage } from '@/lib/api';
import { useApiMutation, useDesks, useIsPhone, useRuntimeProfiles } from '@/lib/hooks';
import { ChatScreen } from '@/components/chat/chat-screen';
import { t, tr } from '@/lib/i18n';
import { ConversationThread, useConversationLive } from '@/components/chat/conversation-thread';
import { defaultRuntime, runtimeHint } from '@/routes/start';
import { qk } from '@/lib/query-keys';
import { useRealtimeEnvelope, useRealtimeSubscription } from '@/lib/realtime';
import { cn } from '@/lib/utils';

const NONE = '__none';
/** Whether the chat next to the view is open (per browser). */
const CHAT_KEY = 'deskworks.viewChat';
const CURATOR: Record<ViewCuratorMode, string> = {
  off: t('Agents may not change it'),
  suggest: t('Agents propose changes, a person accepts them'),
  auto: t('Agents change it directly'),
};

function by(c: { type: string; name: string | null }) {
  return c.type === 'agent' ? (
    <span className="inline-flex items-center gap-1">
      <Bot className="size-3" /> {c.name ?? t('an agent')}
    </span>
  ) : (
    (c.name ?? t('a person'))
  );
}

export function ViewDetailPage() {
  const { workspaceId, viewId } = useParams({ from: '/_authed/w/$workspaceId/views/$viewId' });
  useRealtimeSubscription(workspaceId);
  const queryClient = useQueryClient();
  const view = useQuery({ queryKey: viewKeys.detail(viewId), queryFn: () => api.views.get(viewId) });
  // A new version (from the code tab, an agent or Claude Code via MCP) shows up right away.
  useRealtimeEnvelope((env) => {
    const data = (env.data ?? {}) as { viewId?: string };
    if (env.event === 'view.updated' && data.viewId === viewId) void queryClient.invalidateQueries({ queryKey: viewKeys.detail(viewId) });
  });
  if (view.isPending) return <LoadingPage />;
  if (view.isError) return <div className="mx-auto max-w-3xl px-4 py-8"><ErrorState error={view.error} onRetry={() => void view.refetch()} /></div>;
  return <ViewPage workspaceId={workspaceId} v={view.data} />;
}

function ViewPage({ workspaceId, v }: { workspaceId: string; v: ViewDetail }) {
  const [tab, setTab] = useState<'view' | 'code' | 'prompt'>('view');
  const [showProposal, setShowProposal] = useState(false);
  const [chatOpen, setChatOpen] = useState(() => {
    try {
      return localStorage.getItem(CHAT_KEY) !== 'closed';
    } catch {
      return true;
    }
  });
  const toggleChat = (open: boolean) => {
    setChatOpen(open);
    try {
      localStorage.setItem(CHAT_KEY, open ? 'open' : 'closed');
    } catch {
      /* ignore */
    }
  };
  // On a phone the chat opens over the view, from the button at the bottom (docs/features/mobile.md#chats).
  const phone = useIsPhone();
  const [phoneChat, setPhoneChat] = useState(false);
  const invalidate = [viewKeys.detail(v.id), viewKeys.all(workspaceId)];
  const accept = useApiMutation(() => api.views.accept(v.id, v.proposal!.number), { invalidate, success: t('Proposal accepted') });
  const reject = useApiMutation(() => api.views.reject(v.id, v.proposal!.number), { invalidate, success: t('Proposal rejected') });

  return (
    <Page wide>
      <Link to="/w/$workspaceId/views" params={{ workspaceId }} className="mb-3 inline-flex items-center gap-1 text-[13px] text-muted-foreground hover:text-foreground">
        <ChevronLeft className="size-4" /> {t('Views')}
      </Link>
      <div className="mb-4">
        <div className="mb-2 flex flex-wrap items-center justify-end gap-2">
          <div className="inline-flex rounded-md border p-0.5">
            {(
              [
                ['view', LayoutDashboard, t('View')],
                ['code', Code2, t('Code')],
                ['prompt', MessageSquareText, t('Prompt')],
              ] as const
            ).map(([key, Icon, label]) => (
              <Button key={key} size="xs" variant={tab === key ? 'secondary' : 'ghost'} onClick={() => setTab(key)}>
                <Icon /> {label}
              </Button>
            ))}
          </div>
          {tab === 'view' && !chatOpen && !phone && (
            <Button size="sm" variant="outline" onClick={() => toggleChat(true)}>
              <MessagesSquare /> {t('Chat')}
            </Button>
          )}
          <SettingsDialog workspaceId={workspaceId} v={v} />
        </div>
        <h1 className="text-xl font-semibold tracking-tight sm:text-2xl">{v.title}</h1>
        {v.description && <p className="mt-1 text-sm text-muted-foreground">{v.description}</p>}
        <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[13px] text-muted-foreground">
          <span>
            {t('version {n}', { n: v.currentVersion ?? '' })}
            {v.version && (
              <>
                {' '}
                · {by(v.version.createdBy)} · <RelativeTime value={v.version.createdAt} />
              </>
            )}
          </span>
          {v.deskId && (
            <Link to="/w/$workspaceId/desks/$deskId" params={{ workspaceId, deskId: v.deskId }} className="hover:text-foreground hover:underline">
              {t('on {desk}', { desk: v.deskName ?? '' })}
            </Link>
          )}
          {v.version?.tables.length ? <span className="font-mono text-xs">{t('reads {tables}', { tables: v.version.tables.join(', ') })}</span> : null}
        </div>
      </div>

      {v.status === 'broken' && (
        <div className="mb-4 flex items-start gap-2 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200">
          <AlertTriangle className="mt-0.5 size-4 shrink-0" />
          <div>
            <div className="font-medium">{t('This view no longer fits the database.')}</div>
            <div className="text-xs">{v.statusMessage}</div>
          </div>
        </div>
      )}

      {v.proposal && (
        <div className="mb-4 rounded-lg border border-violet-200 bg-violet-50/60 px-4 py-3 dark:border-violet-900 dark:bg-violet-950/30">
          <div className="flex flex-wrap items-center gap-2 text-sm">
            <Bot className="size-4 text-violet-600" />
            <span className="min-w-0 flex-1">
              {tr('{name} proposes version {n}', { name: <span className="font-medium">{v.proposal.createdBy.name ?? t('An agent')}</span>, n: v.proposal.number })}
              {v.proposal.note ? `: ${v.proposal.note}` : ''}
            </span>
            <Button size="sm" variant="ghost" onClick={() => setShowProposal((s) => !s)}>
              {showProposal ? t('Hide it') : t('Show it')}
            </Button>
            <Button size="sm" variant="outline" onClick={() => reject.mutate()} loading={reject.isPending}>
              <X /> {t('Reject')}
            </Button>
            <Button size="sm" onClick={() => accept.mutate()} loading={accept.isPending}>
              <Check /> {t('Accept')}
            </Button>
          </div>
          {showProposal && (
            <Card className="mt-3 p-4">
              <Preview workspaceId={workspaceId} code={v.proposal.code} />
            </Card>
          )}
        </div>
      )}

      {tab === 'view' && (
        <div className={cn('grid items-start gap-4', chatOpen && !phone && 'lg:grid-cols-[minmax(0,1fr)_400px]')}>
          <Card className="min-w-0 p-5">
            {v.version ? (
              <ViewHost workspaceId={workspaceId} viewId={v.id} code={v.version.code} version={v.currentVersion} tables={v.version.tables} />
            ) : (
              <div className="text-sm text-muted-foreground">{t('No live version yet.')}</div>
            )}
          </Card>
          {chatOpen && !phone && <ViewChat workspaceId={workspaceId} v={v} onClose={() => toggleChat(false)} />}
        </div>
      )}
      {phone && tab === 'view' && (
        <>
          <div aria-hidden className="h-20" />
          <Button
            onClick={() => setPhoneChat(true)}
            size="lg"
            className="fixed right-4 bottom-[calc(1rem+env(safe-area-inset-bottom))] z-30 rounded-full shadow-lg"
          >
            <Sparkles /> {t('Change with the assistant')}
          </Button>
          {phoneChat && <ViewChat workspaceId={workspaceId} v={v} onClose={() => setPhoneChat(false)} screen />}
        </>
      )}
      {tab === 'code' && (
        <div className="grid gap-4">
          <CodeEditor workspaceId={workspaceId} v={v} onSaved={() => setTab('view')} />
          <div>
            <h2 className="mb-2 flex items-center gap-1.5 text-sm font-medium text-muted-foreground">
              <History className="size-4" /> {t('Versions')}
            </h2>
            <Versions workspaceId={workspaceId} v={v} />
          </div>
        </div>
      )}
      {tab === 'prompt' && <PromptTab workspaceId={workspaceId} v={v} />}
    </Page>
  );
}

/**
 * The chat next to the view (docs/features/views.md): the person asks the view assistant for changes in
 * plain words, and every version it saves shows up on the left right away. It is the same chat as the
 * Prompt tab's; the first message starts it on the model chosen here.
 */
function ViewChat({ workspaceId, v, onClose, screen }: { workspaceId: string; v: ViewDetail; onClose: () => void; screen?: boolean }) {
  const queryClient = useQueryClient();
  const state = useQuery({ queryKey: ['view', v.id, 'prompt'], queryFn: () => api.views.prompt(v.id) });
  const profiles = useRuntimeProfiles(workspaceId);
  const [runtimeId, setRuntimeId] = useState<string | null>(null);
  const conversationId = state.data?.conversation?.id ?? null;
  const { query: conv, live } = useConversationLive(conversationId);

  const usable = (profiles.data ?? []).filter((p) => p.status === 'active' && p.implemented);
  const runtime = usable.find((p) => p.id === runtimeId) ?? defaultRuntime(profiles.data ?? []);

  const start = async (message: string) => {
    if (!runtime) throw new Error(t('Choose a model first.'));
    const c = await api.views.chat(v.id, { message, runtimeProfileId: runtime.id });
    queryClient.setQueryData(qk.conversation(c.id), c);
    await queryClient.invalidateQueries({ queryKey: ['view', v.id, 'prompt'] });
  };
  // A new chat: the old one is archived (its waiting assistant stops), the next message starts a fresh one that knows
  // the current kit and nothing of what was said before. The view and its versions stay.
  const fresh = useApiMutation(() => api.conversations.update(conversationId!, { status: 'archived' }), {
    invalidate: [['view', v.id, 'prompt']],
    success: t('New chat: write what should change'),
  });

  const newChat = (
    <Confirm
      title={t('Start a new chat?')}
      description={t('This chat is archived and the view assistant starts fresh with your next message. The view and its versions stay.')}
      confirmLabel={t('New chat')}
      onConfirm={() => fresh.mutateAsync()}
    >
      <Button
        size={screen ? 'icon' : 'icon-sm'}
        variant="ghost"
        aria-label={t('New chat')}
        title={t('New chat')}
        loading={fresh.isPending}
        className={cn(screen && 'size-10')}
      >
        {!fresh.isPending && <Plus />}
      </Button>
    </Confirm>
  );
  const model = (
    <Select value={runtime?.id} onValueChange={setRuntimeId}>
      <SelectTrigger size="sm" className="w-40">
        <SelectValue placeholder={t('Model')} />
      </SelectTrigger>
      <SelectContent>
        {usable.map((p) => (
          <SelectItem key={p.id} value={p.id}>
            {p.name}
            {runtimeHint(p)}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
  const thread =
    state.isPending || (conversationId && conv.isPending) ? (
      <div className="flex flex-1 items-center justify-center text-muted-foreground">
        <Loader2 className="size-4 animate-spin" />
      </div>
    ) : (
      <ConversationThread
        className="min-h-0 flex-1"
        conv={conv.data ?? null}
        agentName={t('View assistant')}
        workspaceId={workspaceId}
        live={live}
        onCreate={start}
        placeholder={t('What should change? e.g. “show it per week”')}
        emptyState={
          <div className="px-2 py-6 text-center text-sm text-muted-foreground">
            {t(
              'Tell the view assistant what to change. It edits the view, tries it with real data and saves a new version, which shows up on the left right away. Every version can be restored under Code.',
            )}
          </div>
        }
      />
    );

  if (screen) {
    return (
      <ChatScreen
        title={t('View assistant')}
        subtitle={
          <>
            {v.title}
            {conv.data?.runtime && <> · {conv.data.runtime}</>}
          </>
        }
        actions={conversationId ? newChat : undefined}
        toolbar={conversationId ? undefined : model}
        onBack={onClose}
      >
        {thread}
      </ChatScreen>
    );
  }

  return (
    <Card className="flex h-[560px] min-w-0 flex-col overflow-hidden lg:sticky lg:top-4 lg:h-[calc(100dvh-11rem)] lg:min-h-[440px]">
      <div className="flex items-center gap-2 border-b px-3 py-2">
        <Sparkles className="size-4 shrink-0 text-primary" />
        <span className="min-w-0 flex-1 truncate text-sm font-medium">{t('View assistant')}</span>
        {conversationId ? (
          <>
            {conv.data?.runtime && <span className="truncate text-xs text-muted-foreground">{conv.data.runtime}</span>}
            {newChat}
          </>
        ) : (
          model
        )}
        <Button size="icon-sm" variant="ghost" onClick={onClose} aria-label={t('Hide chat')} title={t('Hide chat')}>
          <X />
        </Button>
      </div>
      {thread}
    </Card>
  );
}

/** Unsaved code with real data (the preview endpoint), drawn by the same engine. */
function Preview({ workspaceId, code }: { workspaceId: string; code: string }) {
  const preview = useQuery({ queryKey: ['ws', workspaceId, 'viewPreview', code], queryFn: () => api.views.preview(workspaceId, code) });
  if (preview.isPending) return <div className="text-sm text-muted-foreground">{t('Trying it with real data …')}</div>;
  if (preview.isError) return <div className="text-sm text-red-600">{errorMessage(preview.error)}</div>;
  return <PreviewResult workspaceId={workspaceId} code={code} preview={preview.data} />;
}

function PreviewResult({ workspaceId, code, preview }: { workspaceId: string; code: string; preview: ViewPreview }) {
  if (!preview.ok) return <div className="rounded-md bg-red-50 px-3 py-2 font-mono text-xs whitespace-pre-wrap text-red-700 dark:bg-red-950/40 dark:text-red-300">{preview.error}</div>;
  return <ViewHost workspaceId={workspaceId} code={code} data={preview.data} tables={preview.tables} />;
}

function CodeEditor({ workspaceId, v, onSaved }: { workspaceId: string; v: ViewDetail; onSaved: () => void }) {
  const [code, setCode] = useState(v.version?.code ?? '');
  const [tried, setTried] = useState<string | null>(null);
  const [note, setNote] = useState('');
  // The version this editor started from; someone (or an agent) may save a newer one meanwhile.
  const [base, setBase] = useState(v.currentVersion);
  const loadNewest = () => {
    setCode(v.version?.code ?? '');
    setBase(v.currentVersion);
    setTried(null);
  };
  const save = useApiMutation(() => api.views.update(v.id, { code, note: note.trim() || 'Edited by hand' }), {
    invalidate: [viewKeys.detail(v.id), viewKeys.all(workspaceId)],
    success: t('New version is live'),
    onSuccess: (d) => {
      setNote('');
      setBase(d.currentVersion);
      onSaved();
    },
  });
  const changed = code !== (v.version?.code ?? '');
  return (
    <div className="grid gap-4 xl:grid-cols-2">
      <Card className="overflow-hidden">
        <Textarea
          value={code}
          onChange={(e) => setCode(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' && (e.metaKey || e.ctrlKey)) setTried(code);
            if (e.key === 'Tab') {
              e.preventDefault();
              const el = e.currentTarget;
              const s = el.selectionStart;
              setCode(code.slice(0, s) + '  ' + code.slice(el.selectionEnd));
              requestAnimationFrame(() => el.setSelectionRange(s + 2, s + 2));
            }
          }}
          spellCheck={false}
          className="min-h-[480px] resize-y rounded-none border-0 font-mono text-[12.5px] leading-relaxed shadow-none focus-visible:ring-0"
          aria-label={t('View code')}
        />
        <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
          {v.currentVersion !== base && (
            <button type="button" onClick={loadNewest} className="text-xs text-amber-700 underline dark:text-amber-400">
              {t('Version {n} was saved meanwhile – load it', { n: v.currentVersion ?? '' })}
            </button>
          )}
          <Input value={note} onChange={(e) => setNote(e.target.value)} placeholder={t('What changed? (optional)')} className="h-8 min-w-0 flex-1 text-sm" />
          <Button size="sm" variant="outline" onClick={() => setTried(code)} title="⌘↵">
            <Play /> {t('Try')}
          </Button>
          <Button size="sm" onClick={() => save.mutate()} loading={save.isPending} disabled={!changed}>
            <Save /> {t('Save version')}
          </Button>
        </div>
      </Card>
      <Card className="p-4">
        <div className="mb-3 text-xs font-medium tracking-wide text-muted-foreground uppercase">{tried === null ? t('Live version') : t('Try-out (not saved)')}</div>
        {tried === null ? (
          v.version && <ViewHost workspaceId={workspaceId} viewId={v.id} code={v.version.code} version={v.currentVersion} tables={v.version.tables} />
        ) : (
          <Preview workspaceId={workspaceId} code={tried} />
        )}
      </Card>
    </div>
  );
}

function Versions({ workspaceId, v }: { workspaceId: string; v: ViewDetail }) {
  const restore = useApiMutation((n: number) => api.views.restore(v.id, n), {
    invalidate: [viewKeys.detail(v.id), viewKeys.all(workspaceId)],
    success: (d) => t('Version {n} is live again', { n: d.currentVersion ?? '' }),
  });
  return (
    <Card className="divide-y">
      {v.versions.map((x) => (
        <div key={x.number} className={cn('flex flex-wrap items-center gap-3 px-4 py-2.5 text-sm', x.status === 'rejected' && 'opacity-60')}>
          <span className="w-10 font-mono text-xs text-muted-foreground">v{x.number}</span>
          <span className="min-w-0 flex-1 truncate">
            {x.note || <span className="text-muted-foreground">{t('no note')}</span>}
            {x.prompt !== null && <span className="ml-2 text-[11px] text-muted-foreground">{t('from the prompt')}</span>}
          </span>
          <span className="text-xs text-muted-foreground">
            {by(x.createdBy)} · <RelativeTime value={x.createdAt} />
          </span>
          {x.number === v.currentVersion ? (
            <Badge tone="green">{t('live')}</Badge>
          ) : x.status === 'proposed' ? (
            <Badge tone="accent">{t('proposal')}</Badge>
          ) : x.status === 'rejected' ? (
            <Badge tone="muted">{t('rejected')}</Badge>
          ) : (
            <Button size="xs" variant="outline" onClick={() => restore.mutate(x.number)} loading={restore.isPending && restore.variables === x.number}>
              <RotateCcw /> {t('Restore')}
            </Button>
          )}
        </div>
      ))}
    </Card>
  );
}

function SettingsDialog({ workspaceId, v }: { workspaceId: string; v: ViewDetail }) {
  const navigate = useNavigate();
  const desks = useDesks(workspaceId);
  const [open, setOpen] = useState(false);
  const [title, setTitle] = useState(v.title);
  const [description, setDescription] = useState(v.description);
  const [deskId, setDeskId] = useState(v.deskId ?? NONE);
  const [curator, setCurator] = useState<ViewCuratorMode>(v.curatorMode);
  const save = useApiMutation(
    () => api.views.update(v.id, { title, description, deskId: deskId === NONE ? null : deskId, curatorMode: curator }),
    { invalidate: [viewKeys.detail(v.id), viewKeys.all(workspaceId)], success: t('Saved'), onSuccess: () => setOpen(false) },
  );
  const remove = useApiMutation(() => api.views.delete(v.id), {
    invalidate: [viewKeys.all(workspaceId)],
    success: t('View deleted'),
    onSuccess: () => void navigate({ to: '/w/$workspaceId/views', params: { workspaceId } }),
  });
  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button size="sm" variant="outline">
          <Settings2 /> {t('Settings')}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>{t('View settings')}</DialogTitle>
          <DialogDescription>{t('Where it shows up, and how far agents may change it.')}</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <Field label={t('Title')}>
            <Input value={title} onChange={(e) => setTitle(e.target.value)} />
          </Field>
          <Field label={t('Description')} optional>
            <Input value={description} onChange={(e) => setDescription(e.target.value)} />
          </Field>
          <Field label={t('Show on desk')} hint={t('Every view is listed under Views; on a desk it shows on the desk page too.')}>
            <Select value={deskId} onValueChange={setDeskId}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={NONE}>{t('No desk (workspace only)')}</SelectItem>
                {(desks.data ?? []).map((d) => (
                  <SelectItem key={d.id} value={d.id}>
                    {d.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
          <Field label={t('Agents')}>
            <Select value={curator} onValueChange={(x) => setCurator(x as ViewCuratorMode)}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(Object.keys(CURATOR) as ViewCuratorMode[]).map((k) => (
                  <SelectItem key={k} value={k}>
                    {CURATOR[k]}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </Field>
        </div>
        <DialogFooter className="sm:justify-between">
          <Confirm title={t('Delete “{title}”?', { title: v.title })} description={t('The view and all its versions are deleted. The data stays in the database.')} confirmLabel={t('Delete')} destructive onConfirm={() => remove.mutateAsync()}>
            <Button variant="ghost" className="text-destructive hover:text-destructive">
              <Trash2 /> {t('Delete')}
            </Button>
          </Confirm>
          <Button onClick={() => save.mutate()} loading={save.isPending}>
            {t('Save')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

/**
 * The view's prompt (docs/features/views.md): what it should show, in plain
 * words. "Update view" saves it, and the view assistant rewrites the view to
 * match; the live view next to it changes as soon as the new version is saved.
 */
function PromptTab({ workspaceId, v }: { workspaceId: string; v: ViewDetail }) {
  const queryClient = useQueryClient();
  const state = useQuery({ queryKey: ['view', v.id, 'prompt'], queryFn: () => api.views.prompt(v.id) });
  const profiles = useRuntimeProfiles(workspaceId);
  const [prompt, setPrompt] = useState<string | null>(null);
  const [runtimeId, setRuntimeId] = useState<string | null>(null);
  const conversationId = state.data?.conversation?.id ?? null;
  const { query: conv, live } = useConversationLive(conversationId);

  const usable = (profiles.data ?? []).filter((p) => p.status === 'active' && p.implemented);
  const runtime = usable.find((p) => p.id === (runtimeId ?? conv.data?.runtimeProfileId)) ?? defaultRuntime(profiles.data ?? []);
  const text = prompt ?? v.prompt;
  const busy = conv.data?.agentState === 'starting' || conv.data?.agentState === 'thinking' || live.awaitingReply;

  const update = useApiMutation(() => api.views.updatePrompt(v.id, { prompt: text, runtimeProfileId: runtime!.id }), {
    invalidate: [['view', v.id, 'prompt'], viewKeys.detail(v.id)],
    onSuccess: (res) => {
      setPrompt(null);
      live.markSent();
      if (res.conversation) void queryClient.invalidateQueries({ queryKey: ['conversation', res.conversation.id] });
    },
  });

  const madeFrom = v.version?.prompt ?? null;
  const fits = !!v.prompt && madeFrom === v.prompt;
  const messages = (conv.data?.messages ?? []).filter((m) => m.senderType !== 'user');
  return (
    <div className="grid gap-4 xl:grid-cols-[minmax(0,5fr)_minmax(0,7fr)]">
      <div className="grid content-start gap-4">
        <Card className="overflow-hidden">
          <Textarea
            value={text}
            onChange={(e) => setPrompt(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && (e.metaKey || e.ctrlKey) && text.trim() && runtime) update.mutate();
            }}
            rows={8}
            placeholder={t('Describe what this view should show, e.g. “Open cases as a board by status; highlight cases older than 48 hours; the number of new cases per day for the last 30 days.”')}
            className="resize-y rounded-none border-0 text-[14px] leading-relaxed shadow-none focus-visible:ring-0"
            aria-label={t('Prompt')}
          />
          <div className="flex flex-wrap items-center gap-2 border-t px-3 py-2">
            <Select value={runtime?.id} onValueChange={setRuntimeId}>
              <SelectTrigger size="sm" className="w-48">
                <SelectValue placeholder={t('Model')} />
              </SelectTrigger>
              <SelectContent>
                {usable.map((p) => (
                  <SelectItem key={p.id} value={p.id}>
                    {p.name}
                    {runtimeHint(p)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            <span className="flex-1" />
            <Button size="sm" onClick={() => update.mutate()} loading={update.isPending || busy} disabled={!text.trim() || !runtime}>
              <Sparkles /> {t('Update view')}
            </Button>
          </div>
        </Card>

        <div className="flex items-start gap-2 px-1 text-xs text-muted-foreground">
          {busy ? (
            <>
              <Loader2 className="mt-0.5 size-3.5 shrink-0 animate-spin text-primary" />
              <span>{live.activity ?? t('The view assistant is updating the view …')}</span>
            </>
          ) : fits ? (
            <>
              <CircleCheck className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />
              <span>{t('Version {n} was made from this prompt.', { n: v.currentVersion ?? '' })}</span>
            </>
          ) : v.prompt && madeFrom === null ? (
            <span>{t('The live version was changed by hand in Code. “Update view” makes it follow the prompt again.')}</span>
          ) : v.prompt ? (
            <span>{t('The live version was made from an earlier prompt.')}</span>
          ) : (
            <span>{t('No prompt yet. Describe the view and choose “Update view” — the view assistant writes the code.')}</span>
          )}
        </div>

        {messages.length > 0 && (
          <Card className="divide-y">
            {messages.slice(-6).map((m) => (
              <div key={m.id} className="flex gap-2 px-3 py-2 text-[13px]">
                {m.senderType === 'agent' ? <Bot className="mt-0.5 size-3.5 shrink-0 text-primary" /> : <Check className="mt-0.5 size-3.5 shrink-0 text-emerald-600" />}
                <span className={cn('min-w-0 whitespace-pre-wrap', m.senderType === 'system' && 'text-muted-foreground')}>{m.content}</span>
              </div>
            ))}
          </Card>
        )}
      </div>
      <Card className="p-5">
        {v.version && <ViewHost workspaceId={workspaceId} viewId={v.id} code={v.version.code} version={v.currentVersion} tables={v.version.tables} />}
      </Card>
    </div>
  );
}
